import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ReactSlider from 'react-slider';
import StoreLayout from './StoreLayout';
import ProductCard from './ProductCard';
import Sheet from './Sheet';
import { SORTS, fetchProducts, useBrands, useCategories, useCategoryCounts, usePriceRange } from './catalogApi';
import { ChevronIcon, FilterIcon, GridIcon } from './Icons';
import { formatInr, newestIds, useDocumentTitle } from './retailUtils';

const PAGE = 24;
const DENSITY_KEY = 'rt_grid_density';
// The shopper's own choice when they've made one; otherwise the smaller cards (more per row:
// 3 on phones, 4 on tablets, 6 on desktop).
const defaultDensity = () => 'dense';
const readDensity = () => {
  try {
    const saved = localStorage.getItem(DENSITY_KEY);
    return saved === 'dense' || saved === 'roomy' ? saved : defaultDensity();
  } catch { return defaultDensity(); }
};

function RadioGroup({ title, name, options, value, onChange, allLabel }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? options : options.slice(0, 8);
  return (
    <fieldset className="rt-facet">
      <legend>{title}</legend>
      <label className="rt-check rt-facet-opt">
        <input type="radio" name={name} checked={!value} onChange={() => onChange(null)} />
        <span>{allLabel}</span>
      </label>
      {shown.map(v => (
        <label key={v} className="rt-check rt-facet-opt">
          <input type="radio" name={name} checked={value === v} onChange={() => onChange(v)} />
          <span>{v}</span>
        </label>
      ))}
      {options.length > 8 && (
        <button type="button" className="rt-link" onClick={() => setExpanded(e => !e)}>
          {expanded ? 'Show less' : `+ ${options.length - 8} more`}
        </button>
      )}
    </fieldset>
  );
}

/** The product listing — the retail store's listing (tabs, filter drawer, quick filters, chips,
 *  sort, grid density, infinite scroll), filtered and paged by the server since the catalogue is
 *  too big to load at once. Every filter lives in the URL: shareable, and restored on Back. */
export default function StoreCatalogPage() {
  const [params, setParams] = useSearchParams();
  const categories = useCategories();
  const [filterSheet, setFilterSheet] = useState(null); // null | 'all' | one quick-filter key
  const [sortOpen, setSortOpen] = useState(false);
  const [density, setDensity] = useState(readDensity);
  const [slider, setSlider] = useState(null);
  const [result, setResult] = useState({ key: null, products: [], total: 0, page: 0, last: true, loading: true, error: '' });
  const sentinel = useRef(null);
  const resultsTop = useRef(null);

  const category = params.get('category') || 'All';
  const subcategory = params.get('subcategory') || null;
  const brand = params.get('brand') || null;
  const q = params.get('q') || '';
  const sort = SORTS[params.get('sort')] ? params.get('sort') : 'featured';
  const min = Number(params.get('min')) || null;
  const max = Number(params.get('max')) || null;
  const filters = useMemo(() => ({ category, subcategory, brand, q, min, max, sort }), [category, subcategory, brand, q, min, max, sort]);
  const key = JSON.stringify(filters);

  // Where the shopper is, always in full: the listing's context (a brand, Deals, New arrivals or a
  // search) and the category/type within it — in the breadcrumb trail and in the heading, e.g.
  // Home › Brands › EYANTRA › Stationery and "EYANTRA · Stationery".
  const context = brand ? { label: brand, parent: { label: 'Brands', to: '/brands' }, params: { brand } }
    : sort === 'discount' ? { label: 'Deals', params: { sort } }
    : q ? { label: `Results for “${q}”`, params: { q } }
    : sort === 'newest' ? { label: 'New arrivals', params: { sort } }
    : null;
  const listingLink = (extra) => {
    const p = new URLSearchParams({ ...(context?.params || {}), ...extra });
    return `/catalog${p.toString() ? `?${p}` : ''}`;
  };
  const place = subcategory || (category !== 'All' ? category : null);
  const heading = [context?.label, place].filter(Boolean).join(' · ') || 'Shop all';
  const crumbs = [
    { label: 'Home', to: '/' },
    ...(context?.parent ? [context.parent] : []),
    ...(context ? [{ label: context.label, to: listingLink({}) }] : []),
    ...(category !== 'All' ? [{ label: category, to: listingLink({ category }) }] : []),
    ...(subcategory ? [{ label: subcategory, to: null }] : []),
  ];
  useDocumentTitle(q && !place ? `Search: ${q}` : heading);

  const brands = useBrands(category);
  const bounds = usePriceRange({ category, subcategory, brand, q });
  // A listing narrowed to a brand, a search or Deals offers only the categories and types that
  // have products in it (not every category in the store), and its tabs keep that narrowing.
  // New arrivals is the whole catalogue in another order, so it keeps the full list.
  const deals = sort === 'discount';
  const scoped = !!(brand || q || deals);
  const scopedCategories = useCategoryCounts(scoped ? { brand, q, deals } : null);
  const tabCategories = scoped ? (scopedCategories || []) : categories;
  const types = tabCategories.find(c => c.name === category)?.subcategories || [];

  // First page whenever the filters change; more pages as the shopper scrolls.
  useEffect(() => {
    let alive = true;
    setResult(r => ({ ...r, key, loading: true, error: '' }));
    fetchProducts(filters, 0, PAGE)
      .then(r => alive && setResult({ key, products: r.products, total: r.total, page: 0, last: r.last, loading: false, error: '' }))
      .catch(() => alive && setResult({ key, products: [], total: 0, page: 0, last: true, loading: false, error: 'Could not load products. Please refresh the page.' }));
    return () => { alive = false; };
    // filters is derived from key
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  const loadMore = () => {
    if (result.loading || result.last || result.key !== key) return;
    const next = result.page + 1;
    setResult(r => ({ ...r, loading: true }));
    fetchProducts(filters, next, PAGE)
      .then(r => setResult(cur => cur.key !== key ? cur : ({ ...cur, products: [...cur.products, ...r.products], page: next, last: r.last, loading: false })))
      .catch(() => setResult(cur => ({ ...cur, loading: false, last: true })));
  };
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) loadMoreRef.current(); }, { rootMargin: '600px' });
    io.observe(el);
    return () => io.disconnect();
  }, [result.products.length, result.last]);

  const update = (changes) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) => { if (v == null || v === '') next.delete(k); else next.set(k, String(v)); });
    setParams(next, { replace: true });
    // Filtering or sorting from far down the list goes back to the first result.
    const top = resultsTop.current && resultsTop.current.getBoundingClientRect().top + window.scrollY
      - (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--rt-header-h')) || 0);
    if (top != null && window.scrollY > top) window.scrollTo({ top });
  };
  const changeDensity = (d) => { setDensity(d); try { localStorage.setItem(DENSITY_KEY, d); } catch { /* per-viewer convenience only */ } };

  const priceFiltered = !!(min || max);
  const chips = [
    ...(subcategory ? [{ label: subcategory, clear: () => update({ subcategory: null }) }] : []),
    ...(brand ? [{ label: brand, clear: () => update({ brand: null }) }] : []),
    ...(priceFiltered ? [{ label: `${formatInr(min || bounds?.[0] || 0)} – ${max ? formatInr(max) : 'any'}`, clear: () => update({ min: null, max: null }) }] : []),
    ...(q ? [{ label: `“${q}”`, clear: () => update({ q: null }) }] : []),
  ];
  const filterCount = chips.filter(c => !c.label.startsWith('“')).length;
  const clearFilters = () => update({ subcategory: null, brand: null, min: null, max: null });

  const quick = [
    ...(types.length > 1 ? [{ key: 'type', label: 'Type', active: !!subcategory }] : []),
    ...(bounds && bounds[1] > bounds[0] ? [{ key: 'price', label: 'Price', active: priceFiltered }] : []),
    ...(brands.length > 1 ? [{ key: 'brand', label: 'Brand', active: !!brand }] : []),
  ];
  const show = (k) => filterSheet === 'all' || filterSheet === k;
  const lo = Math.max(bounds?.[0] || 0, min || bounds?.[0] || 0);
  const hi = Math.min(bounds?.[1] || 0, max || bounds?.[1] || 0);
  const panel = (
    <div className="rt-filters">
      {show('type') && types.length > 1 && (
        <RadioGroup title="Type" name="sf-type" options={types} value={subcategory} allLabel="All types"
          onChange={v => update({ subcategory: v, brand: null })} />
      )}
      {show('price') && bounds && bounds[1] > bounds[0] && (
        <fieldset className="rt-facet">
          <legend>Price (MRP)</legend>
          <div style={{ padding: '14px 10px 4px' }}>
            <ReactSlider
              className="rt-slider" thumbClassName="rt-slider-thumb" trackClassName="rt-slider-track"
              value={slider || [lo, hi]} min={bounds[0]} max={bounds[1]} pearling minDistance={1}
              ariaLabel={['Minimum price', 'Maximum price']}
              onChange={setSlider}
              onAfterChange={([a, b]) => { setSlider(null); update({ min: a > bounds[0] ? a : null, max: b < bounds[1] ? b : null }); }}
            />
          </div>
          <div className="rt-facet-range">{formatInr((slider || [lo])[0])} – {formatInr((slider || [0, hi])[1])}</div>
        </fieldset>
      )}
      {show('brand') && brands.length > 1 && (
        <RadioGroup title="Brand" name="sf-brand" options={brands} value={brand} allLabel="All brands"
          onChange={v => update({ brand: v })} />
      )}
    </div>
  );

  const stale = result.key !== key;
  const products = useMemo(() => (stale ? [] : result.products), [stale, result.products]);
  const newIds = useMemo(() => newestIds(products), [products]);
  const firstLoad = result.loading && (stale || products.length === 0);

  return (
    <StoreLayout wide>
      <div className="rt-plp-head">
        {crumbs.length > 1 && (
          <nav className="rt-crumbs sf-plp-crumbs" aria-label="Breadcrumb">
            {/* Phones show only the parent link ("‹ Stationery"): the heading already names the
                brand and category, and the full trail would wrap onto two lines. */}
            {crumbs.map((c, i) => {
              const last = i === crumbs.length - 1;
              const parent = i === crumbs.length - 2;
              return (
                <React.Fragment key={`${c.label}-${i}`}>
                  {last ? <span aria-current="page" className="sf-crumb-rest">{c.label}</span>
                    : <Link to={c.to} className={parent ? 'sf-crumb-parent' : 'sf-crumb-rest'}>{c.label}</Link>}
                  {!last && <span className="sf-crumb-rest" aria-hidden="true">›</span>}
                </React.Fragment>
              );
            })}
          </nav>
        )}
        <h1 className="rt-display">{heading}</h1>
        {category !== 'All' && types.length > 1 ? (
          <div className="rt-tabs" role="group" aria-label="Types">
            {[null, ...types].map(s => (
              <button key={s || 'all'} type="button" className="rt-tab" aria-pressed={subcategory === s}
                onClick={() => update({ subcategory: s, ...(scoped ? {} : { brand: null }), min: null, max: null })}>
                {s || `All ${category}`}
              </button>
            ))}
          </div>
        ) : tabCategories.length > 1 && (
          <div className="rt-tabs" role="group" aria-label="Categories">
            {['All', ...tabCategories.map(c => c.name)].map(c => (
              <button key={c} type="button" className="rt-tab" aria-pressed={category === c}
                onClick={() => update({ category: c === 'All' ? null : c, subcategory: null, ...(scoped ? {} : { brand: null }), min: null, max: null })}>
                {c}
              </button>
            ))}
          </div>
        )}
      </div>

      <div ref={resultsTop} aria-hidden="true" />
      <div className="rt-plp-bar">
        <div className="rt-plp-bar-left">
          <button type="button" className="rt-text-link rt-bar-btn" onClick={() => setFilterSheet('all')}>
            <FilterIcon /> Filter{filterCount ? ` (${filterCount})` : ''}
          </button>
          <div className="rt-sort">
            <button type="button" className="rt-text-link rt-bar-btn" aria-haspopup="listbox" aria-expanded={sortOpen} onClick={() => setSortOpen(o => !o)}>
              <span>Sort{sort !== 'featured' && <span className="sf-sort-value">: {SORTS[sort].label}</span>}</span> <ChevronIcon />
            </button>
            {sortOpen && (
              <ul className="rt-sort-menu" role="listbox" aria-label="Sort by">
                {Object.entries(SORTS).map(([k, s]) => (
                  <li key={k}>
                    <button type="button" role="option" aria-selected={sort === k}
                      onClick={() => { update({ sort: k === 'featured' ? null : k }); setSortOpen(false); }}>
                      {s.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <span className="rt-count" aria-live="polite">{firstLoad ? 'Loading…' : `${result.total} product${result.total !== 1 ? 's' : ''}`}</span>
        <div className="rt-density" role="group" aria-label="Grid size">
          <button type="button" aria-pressed={density === 'roomy'} aria-label="Larger photos" onClick={() => changeDensity('roomy')}><GridIcon cols={2} /></button>
          <button type="button" aria-pressed={density === 'dense'} aria-label="More products per row" onClick={() => changeDensity('dense')}><GridIcon cols={4} /></button>
        </div>
      </div>

      {(quick.length > 0 || chips.length > 0) && (
        <div className="rt-quick" role="group" aria-label="Quick filters">
          {quick.map(f => (
            <button key={f.key} type="button" className={`rt-quick-btn${f.active ? ' is-on' : ''}`} onClick={() => setFilterSheet(f.key)}>
              {f.label} <ChevronIcon size={12} />
            </button>
          ))}
          {chips.map(c => <button key={c.label} type="button" className="rt-chip" onClick={c.clear} aria-label={`Remove filter ${c.label}`}>{c.label} ✕</button>)}
          {filterCount > 1 && <button type="button" className="rt-link" onClick={clearFilters}>Clear all</button>}
        </div>
      )}

      {result.error && <div className="rt-alert rt-alert--error rt-pad">{result.error}</div>}
      {firstLoad ? (
        <div className={`rt-grid rt-grid--${density}`} aria-hidden="true">
          {[...Array(8)].map((_, i) => (
            <div key={i}>
              <div className="rt-skel" style={{ aspectRatio: '3 / 4', borderRadius: 0 }} />
              <div className="rt-skel" style={{ height: 12, width: '60%', margin: '12px 10px 0' }} />
              <div className="rt-skel" style={{ height: 12, width: '30%', margin: '8px 10px 0' }} />
            </div>
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="rt-empty">
          <h2>No products found</h2>
          <p>Try a different search or remove a filter.</p>
          {(filterCount > 0 || q) && (
            <button type="button" className="rt-btn rt-btn--ghost" style={{ marginTop: 12 }}
              onClick={() => setParams(category !== 'All' ? { category } : {}, { replace: true })}>
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <>
          <div className={`rt-grid rt-grid--${density}`}>
            {products.map((p, i) => <ProductCard key={p.id} product={p} priority={i < 4} isNew={newIds.has(p.id)} />)}
          </div>
          {!result.last && (
            <div ref={sentinel} className="rt-more">
              <button type="button" className="rt-btn rt-btn--ghost" disabled={result.loading} onClick={loadMore}>
                {result.loading ? 'Loading…' : `Show more (${result.total - products.length} left)`}
              </button>
            </div>
          )}
        </>
      )}

      <Sheet open={!!filterSheet} onClose={() => setFilterSheet(null)} side="right"
        title={filterSheet === 'all' ? 'Filter' : quick.find(f => f.key === filterSheet)?.label || 'Filter'}
        footer={
          <div className="rt-qv-actions">
            <button type="button" className="rt-btn rt-btn--ghost" onClick={clearFilters} disabled={!filterCount}>Clear all</button>
            <button type="button" className="rt-btn" onClick={() => setFilterSheet(null)}>Show {result.total} product{result.total !== 1 ? 's' : ''}</button>
          </div>
        }>
        {panel}
      </Sheet>
    </StoreLayout>
  );
}
